/**
 * Capa de Acceso a Datos (Data Access Logic - DL) para la dinámica "Light Show Pro".
 * Gestiona las peticiones HTTP (fetch) hacia el controlador LightShowController de la API.
 */

const Prepare = async (beatsPerMinute, framesSequence, targetZones = null, presetName = null, flashActive = true, screenActive = true, offColor = '#000000') => {
    try {
        const dataToSend = {
            beatsPerMinute: beatsPerMinute,
            frames: framesSequence,
            targetZones: targetZones,
            presetName: presetName,
            flashActive: flashActive,
            screenActive: screenActive,
            offColor: offColor
        }
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}LightShow/prepare`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(dataToSend)
        })
        
        if (!response.ok) {
            const errorData = await response.json()
            return { 
                success: false, 
                message: errorData.message || 'No ha sido posible preparar el show.' 
            }
        }
        
        const data = await response.json()
        return { success: true, data: data }
    } catch (error) {
        return { success: false, message: 'Error de red al intentar preparar el show.' }
    }
}

const Start = async (beatsPerMinute, framesSequence, targetZones = null, presetName = null, flashActive = true, screenActive = true, offColor = '#000000') => {
    try {
        const dataToSend = {
            beatsPerMinute: beatsPerMinute,
            frames: framesSequence,
            targetZones: targetZones,
            presetName: presetName,
            flashActive: flashActive,
            screenActive: screenActive,
            offColor: offColor
        };

        const response = await fetch(`${process.env.REACT_APP_API_URL}LightShow/start`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(dataToSend)
        })
        
        if (!response.ok) {
            const errorData = await response.json()
            return { 
                success: false, 
                message: errorData.message || 'No ha sido posible arrancar el show de luces.' 
            }
        }
        
        const data = await response.json()
        return { success: true, data: data }
    } catch (error) {
        return { success: false, message: 'Error de red al intentar iniciar el show de luces.' }
    }
}

const Stop = async (targetZones = null) => {
    try {
        const dataToSend = {
            targetZones: targetZones
        };

        const response = await fetch(`${process.env.REACT_APP_API_URL}LightShow/stop`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(dataToSend)
        })
        
        if (!response.ok) {
            const errorData = await response.json()
            return { 
                success: false, 
                message: errorData.message || 'No ha sido posible detener la dinámica.' 
            }
        }
        
        const data = await response.json()
        return { success: true, data: data }
    } catch (error) {
        return { success: false, message: 'Error de red al intentar detener el show.' }
    }
}

const GetAllPresets = async () => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}LightShow/presets`);
        if (!response.ok) throw new Error('Error al obtener presets');
        const data = await response.json();
        return { success: true, data: data };
    } catch (error) {
        return { success: false, message: 'Error de red al cargar los presets de la base de datos.' };
    }
}

const CreatePreset = async (name, bpm, framesSequence) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}LightShow/presets`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: name, beatsPerMinute: bpm, framesJson: JSON.stringify(framesSequence) })
        });
        const data = await response.json();
        return { success: response.ok, data: data.data, message: data.message };
    } catch (error) {
        return { success: false, message: 'Error de red al crear el preset.' };
    }
}

const UpdatePreset = async (id, name, bpm, framesSequence) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}LightShow/presets/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: name, beatsPerMinute: bpm, framesJson: JSON.stringify(framesSequence) })
        });
        const data = await response.json();
        return { success: response.ok, message: data.message };
    } catch (error) {
        return { success: false, message: 'Error de red al actualizar el preset.' };
    }
}

const DeletePreset = async (id) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}LightShow/presets/${id}`, {
            method: 'DELETE'
        });
        const data = await response.json();
        return { success: response.ok, message: data.message };
    } catch (error) {
        return { success: false, message: 'Error de red al eliminar el preset.' };
    }
}

const GetLightShowStats = async () => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}LightShow/stats`);
        if (!response.ok) throw new Error('Error al obtener stats');
        const data = await response.json();
        return { success: true, data: data };
    } catch (error) {
        return { success: false, message: 'Error de red al cargar estadísticas del show.' };
    }
}

const GetSettings = async () => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}LightShow/settings`);
        if (!response.ok) throw new Error('Error al obtener ajustes');
        const data = await response.json();
        return { success: true, data: data };
    } catch (error) {
        return { success: false, message: 'Error de red al cargar los ajustes de la base de datos.' };
    }
}

const SaveSettings = async (settings) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}LightShow/settings`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(settings)
        });
        const data = await response.json();
        return { success: response.ok, message: data.message };
    } catch (error) {
        return { success: false, message: 'Error de red al guardar los ajustes.' };
    }
}

const ApplySettings = async (settings) => {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}LightShow/apply`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(settings)
        });
        const data = await response.json();
        return { success: response.ok, message: data.message };
    } catch (error) {
        return { success: false, message: 'Error de red al intentar aplicar los ajustes.' };
    }
}

export { Prepare, Start, Stop, GetAllPresets, CreatePreset, UpdatePreset, DeletePreset, GetLightShowStats, GetSettings, SaveSettings, ApplySettings };